import { useParams } from '@tanstack/react-router';
import useSWR, { mutate as globalMutate } from 'swr';
import { backendClient } from '@/lib/api-client';

export interface Tag {
  guid: string;
  name: string;
  description: string | null;
  color: string | null;
  created_at: string;
}

// Custom fetcher that uses our apiClient with authentication
const fetcher = async (url: string) => {
  const response = await backendClient.get<Tag[]>(url);
  // Ensure that all tags have a color property, defaulting to #CCCCCC if missing
  const tags = response.data.map((tag: Tag) => ({
    ...tag,
    color: tag.color || '#CCCCCC'
  }));
  return tags;
};

export function useTags(guid?: string | null) {
  const { slug } = useParams({ strict: false });
  const routeSlug = typeof slug === 'string' && slug && slug !== 'undefined' ? slug : undefined;
  const matterSlug = guid === null ? undefined : guid || routeSlug;
  
  // Use the correct endpoint format that matches the backend
  const url = matterSlug && matterSlug !== 'undefined' ? `/tags/${matterSlug}` : null;
  
  // Use SWR to handle fetching, caching, and revalidation
  const { data, error, isLoading } = useSWR<Tag[]>(
    url,
    fetcher,
    {
      revalidateOnFocus: false,
      errorRetryCount: 2,
      dedupingInterval: 5000 // 5 seconds
    }
  );
  
  return {
    tags: data || [],
    isLoading,
    isError: !!error,
    mutateTags: () => globalMutate(url)
  };
}
